/**
 * Katalogdan Ekle — ortak katalogdan kiracının kendi stoğuna ürün aktarma ekranı.
 *
 * Tasarımın ana kuralı: katalog 23 binden fazla ürün taşıyor, bu yüzden ürünler ASLA
 * kendiliğinden yüklenmez. Kullanıcı ya arama yapar ya da sektör → kategori → alt
 * kategori zincirini sonuna kadar iner. Üst kategoride durursa ürünler listelenmez;
 * yalnızca yaprak (alt kategori) seçimi tabloyu açar.
 *
 * İki aktarma yolu var ve ikisi aynı sepette birleşir:
 *   • Tekil — tablodan ürün işaretleme (arama sonucundan veya yaprak listesinden).
 *   • Toplu — kategori/alt kategori işaretleme; ürünler listelenmeden, aktarma anında
 *     sayfa sayfa çözülür.
 *
 * Aktarılan ürünler 0 fiyatla ve varsayılan 1 adetle gelir; katalogda fiyat yoktur,
 * satış fiyatını kiracı sonradan girer.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import GuideButton from '../../../common/guide/GuideButton';

import {
  CatalogBrowseService,
  buildCategoryTree,
  CategoryService,
  InventoryService,
  WarehouseService,
} from '../services';
import type {
  BulkCreateItemDto,
  CatalogBrowseProduct,
  CatalogBrowseSector,
  CatalogCategoryNode,
  InventoryCategoryDto,
  InventoryWarehouseDto,
} from '../services';

// ════════════════════════════════════════════════════════════════
// Sabitler
// ════════════════════════════════════════════════════════════════

/**
 * Tek bulk-create çağrısındaki ürün adedi.
 *
 * Backend her çağrıda şirketin TÜM stok kartlarını mükerrer kontrolü için belleğe
 * çekiyor. Bu yüzden küçük batch = aynı pahalı okumanın defalarca tekrarı. 500,
 * "sistemi yormadan ama yavaş da olmadan" dengesinin oturduğu yer: 5.000 ürün 10
 * çağrıda biter, tek istek de aşırı büyümez.
 */
const TRANSFER_BATCH_SIZE = 500;

/** Tek seferde aktarılabilecek üst sınır — kazara tüm sektörü seçmeye karşı emniyet. */
const MAX_TRANSFER_COUNT = 5000;

const DEFAULT_QUANTITY = 1;

/** Katalogda fiyat yoktur; kiracı satış fiyatını sonradan girer. */
const TRANSFER_SALE_PRICE = 0;

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

/** Katalog "Kdv Dahil" / "Kdv Hariç" yazar, envanter "included" / "excluded" bekler. */
function mapVatStatus(catalogStatus: string | null | undefined): string {
  return (catalogStatus ?? '').toLowerCase().includes('hariç') ? 'excluded' : 'included';
}

/** Katalog `categoryPath` alanını "Üst > Alt" biçiminden seviye dizisine çevirir. */
function splitCategoryPath(path: string | null | undefined): string[] {
  return (path ?? '')
    .split('>')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/** Kategori adı eşleştirmesi — "İÇECEK" ile "içecek" aynı kategoridir. */
function normalizeCategoryName(name: string): string {
  return name.trim().toLocaleLowerCase('tr');
}

/**
 * Ürünün envanterde açılacak kategori zinciri: sektör → kategori → alt kategori.
 *
 * Katalog `categoryPath` alanını zaten "Üst > Alt" biçiminde veriyor; başına sektörü
 * ekleyerek üç kademeli ağacı kuruyoruz. Sektörü veya kategorisi olmayan ürünlerde
 * eksik seviyeler düşer, zincir kısalır.
 */
function buildProductCategoryPath(product: CatalogBrowseProduct): string {
  return [product.sectorName, product.categoryPath]
    .map((part) => (part ?? '').trim())
    .filter((part) => part.length > 0)
    .join(' > ');
}

/**
 * Katalog kategori yollarını kiracının envanter kategorilerine bağlar; eksik olanları
 * hiyerarşiyi koruyarak açar ve "yol → kategori id" eşlemesi döndürür.
 *
 * Eşleştirme yalnızca ADA göre yapılır, üst kategoriye bakılmaz. Sebebi backend:
 * envanter kategorilerinde benzersizlik kontrolü (CompanyId + Name) üzerinden işliyor
 * ve parent'ı hesaba katmıyor, yani aynı ad ikinci kez açılamıyor (409 döner). Bu
 * yüzden kiracıda "Atıştırmalık" başka bir üst kategori altında zaten varsa yenisi
 * açılmaz, mevcut olan kullanılır.
 */
async function resolveCategoryIds(
  companyId: string,
  paths: string[],
  onCategoryCreated?: (created: number, total: number) => void
): Promise<Map<string, string>> {
  const chains = new Map<string, string[]>();
  paths.forEach((path) => {
    const chain = splitCategoryPath(path);
    if (chain.length > 0) chains.set(path, chain);
  });
  if (chains.size === 0) return new Map();

  const existing = await CategoryService.getAllCategories(companyId);
  const idByName = new Map<string, string>();
  existing.forEach((category) => idByName.set(normalizeCategoryName(category.name), category.id));

  // Açılacak kategori sayısını önden çıkar ki ilerleme göstergesi anlamlı olsun.
  const missing = new Set<string>();
  chains.forEach((chain) =>
    chain.forEach((name) => {
      const key = normalizeCategoryName(name);
      if (!idByName.has(key)) missing.add(key);
    })
  );

  let created = 0;
  const resolved = new Map<string, string>();

  for (const entry of Array.from(chains.entries())) {
    const [path, chain] = entry;
    let parentId: string | null = null;
    let deepestId: string | undefined;

    for (const name of chain) {
      const key = normalizeCategoryName(name);
      let id: string | undefined = idByName.get(key);

      if (!id) {
        let newId: string | undefined;
        try {
          const newCategory: InventoryCategoryDto = await CategoryService.createCategory({
            companyId,
            name: name.trim(),
            parentId,
          });
          newId = newCategory.id;
          created += 1;
          onCategoryCreated?.(created, missing.size);
        } catch (err: any) {
          // 409 = ad zaten kullanımda. Taze listeden yakalamayı dene; yine
          // bulunamazsa bu seviye atlanır. Var olmayan bir id göndermek
          // bulk-create'te FK ihlaline düşüp TÜM partiyi kaybettirirdi.
          if (err?.response?.status === 409) {
            const fresh = await CategoryService.getAllCategories(companyId);
            fresh.forEach((c) => idByName.set(normalizeCategoryName(c.name), c.id));
            newId = idByName.get(key);
          }
          if (!newId) {
            console.error('Kategori oluşturulamadı, bu seviye atlanıyor:', name, err);
          }
        }

        if (!newId) break;
        id = newId;
        idByName.set(key, id);
      }

      deepestId = id;
      parentId = id;
    }

    if (deepestId) resolved.set(path, deepestId);
  }

  return resolved;
}

type SearchMode = 'name' | 'barcode';

interface TransferProgress {
  phase: string;
  current: number;
  total: number;
}

interface TransferResult {
  created: number;
  failed: { name: string; stockCode: string; error: string }[];
}

export interface CatalogPickerModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  /** Aktarım bitince envanter listesi tazelensin. */
  onTransferred: () => void;
}

const CatalogPickerModal: React.FC<CatalogPickerModalProps> = ({
  open,
  onClose,
  companyId,
  onTransferred,
}) => {
  // ─── Gezinme ──────────────────────────────────────
  const [sectors, setSectors] = useState<CatalogBrowseSector[]>([]);
  const [sectorsLoading, setSectorsLoading] = useState(false);
  const [selectedSectorId, setSelectedSectorId] = useState<number | null>(null);

  const [categoryTree, setCategoryTree] = useState<CatalogCategoryNode[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [expandedRoots, setExpandedRoots] = useState<Set<number>>(new Set());

  /** Tabloyu açan yaprak. Sadece burada ürün listesi yüklenir. */
  const [activeLeafId, setActiveLeafId] = useState<number | null>(null);

  /** Sol gezinme paneli (kaydırma kabı) ve içindeki kategori bölümünün başlangıcı. */
  const navPanelRef = useRef<HTMLDivElement | null>(null);
  const categorySectionRef = useRef<HTMLDivElement | null>(null);

  // ─── Arama ────────────────────────────────────────
  const [searchMode, setSearchMode] = useState<SearchMode>('name');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // ─── Ürün tablosu ─────────────────────────────────
  const [products, setProducts] = useState<CatalogBrowseProduct[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [productsLoading, setProductsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // ─── Seçim ────────────────────────────────────────
  /** Tekil seçilen ürünler; sayfalar arasında korunsun diye Map. */
  const [pickedProducts, setPickedProducts] = useState<Map<number, CatalogBrowseProduct>>(new Map());
  /** Toplu seçilen kategoriler (yaprak veya üst). */
  const [pickedCategories, setPickedCategories] = useState<Map<number, CatalogCategoryNode>>(
    new Map()
  );

  // ─── Aktarım ──────────────────────────────────────
  const [warehouses, setWarehouses] = useState<InventoryWarehouseDto[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [quantity, setQuantity] = useState<string>(String(DEFAULT_QUANTITY));
  const [transferring, setTransferring] = useState(false);
  const [progress, setProgress] = useState<TransferProgress | null>(null);
  const [result, setResult] = useState<TransferResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resetAll = useCallback(() => {
    setSelectedSectorId(null);
    setCategoryTree([]);
    setExpandedRoots(new Set());
    setActiveLeafId(null);
    setSearchInput('');
    setSearchTerm('');
    setProducts([]);
    setTotalCount(0);
    setPage(0);
    setPickedProducts(new Map());
    setPickedCategories(new Map());
    setProgress(null);
    setResult(null);
    setError(null);
  }, []);

  const handleClose = () => {
    if (transferring) return;
    if (result && result.created > 0) onTransferred();
    resetAll();
    onClose();
  };

  // ─── Açılışta sektör ve depoları yükle ────────────
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setSectorsLoading(true);
    CatalogBrowseService.getSectors()
      .then((data) => {
        if (!cancelled) setSectors(data);
      })
      .catch((err) => {
        console.error('Katalog sektörleri yüklenemedi:', err);
        if (!cancelled) setError('Katalog sektörleri yüklenemedi.');
      })
      .finally(() => {
        if (!cancelled) setSectorsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !companyId) return;

    let cancelled = false;
    WarehouseService.getAllWarehouses(companyId)
      .then((list) => {
        if (cancelled) return;
        const usable = list.filter((w) => w.isActive && !w.isDeleted);
        setWarehouses(usable);
        // Merkez/varsayılan depo öne çıkar; kullanıcı çoğunlukla oraya aktarıyor.
        const preferred = usable.find((w) => w.isDefault) ?? usable[0];
        if (preferred) setWarehouseId((prev) => prev || preferred.id);
      })
      .catch((err) => console.error('Depolar yüklenemedi:', err));

    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  // ─── Arama debounce ───────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ─── Sektör seçimi → kategori ağacı ───────────────
  useEffect(() => {
    if (selectedSectorId == null) {
      setCategoryTree([]);
      return;
    }

    let cancelled = false;
    setCategoriesLoading(true);
    setExpandedRoots(new Set());
    setActiveLeafId(null);

    CatalogBrowseService.getCategories(selectedSectorId)
      .then((list) => {
        if (!cancelled) setCategoryTree(buildCategoryTree(list));
      })
      .catch((err) => {
        console.error('Kategoriler yüklenemedi:', err);
        if (!cancelled) setError('Kategoriler yüklenemedi.');
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedSectorId]);

  // ─── Kategori bölümüne otomatik kaydırma ──────────
  // Kategoriler sektör ızgarasının ALTINDA açılıyor. Sektör sayısı panele sığmadığında
  // yeni içerik görünür alanın dışında kalıyor ve kullanıcı tıklamanın işe yaradığını
  // göremiyor. scrollIntoView yerine paneli elle kaydırıyoruz: scrollIntoView üstteki
  // dialog gövdesini de kaydırıp modalı yerinden oynatıyor.
  useEffect(() => {
    if (selectedSectorId == null || categoriesLoading) return;

    const panel = navPanelRef.current;
    const target = categorySectionRef.current;
    if (!panel || !target) return;

    const delta = target.getBoundingClientRect().top - panel.getBoundingClientRect().top;
    panel.scrollTo({ top: panel.scrollTop + delta - 8, behavior: 'smooth' });
  }, [selectedSectorId, categoriesLoading]);

  // ─── Ürün listesi ─────────────────────────────────
  // Kural: yalnızca arama varsa VEYA bir yaprak seçiliyse yüklenir. Aksi hâlde istek
  // bile atılmaz — üst kategoride durmuş kullanıcı için 23 binlik tarama yapılmaz.
  const canListProducts = searchTerm.length > 0 || activeLeafId != null;

  // Filtre değişince ilk sayfaya dönülür ve bu RENDER SIRASINDA yapılır. Bir effect'e
  // bırakılsaydı önce eski sayfa numarasıyla bir istek gider, ardından page=0 ile ikincisi
  // giderdi; render sırasında set edilince React bu render'ı atıp yeniden çiziyor ve
  // aşağıdaki fetch effect'i yalnızca bir kez, doğru sayfayla çalışıyor.
  const filterKey = `${searchTerm}|${searchMode}|${selectedSectorId}|${activeLeafId}`;
  const previousFilterKey = useRef(filterKey);
  if (previousFilterKey.current !== filterKey) {
    previousFilterKey.current = filterKey;
    if (page !== 0) setPage(0);
  }

  useEffect(() => {
    if (!open) return;

    if (!canListProducts) {
      setProducts([]);
      setTotalCount(0);
      return;
    }

    let cancelled = false;
    setProductsLoading(true);

    CatalogBrowseService.getProductsPaged({
      page: page + 1, // MUI 0-index, backend 1-index
      pageSize: rowsPerPage,
      search: searchTerm || undefined,
      searchField: searchTerm ? searchMode : undefined,
      // Arama tüm katalogda çalışsın diye kategori kısıtı uygulanmaz; kullanıcı
      // barkodu bilip aradığında hangi kategoride olduğunu bilmek zorunda değil.
      sectorId: searchTerm ? undefined : selectedSectorId ?? undefined,
      categoryId: searchTerm ? undefined : activeLeafId ?? undefined,
    })
      .then((result) => {
        if (cancelled) return;
        setProducts(result.items);
        setTotalCount(result.totalCount);
      })
      .catch((err) => {
        console.error('Katalog ürünleri yüklenemedi:', err);
        if (!cancelled) setError('Katalog ürünleri yüklenemedi.');
      })
      .finally(() => {
        if (!cancelled) setProductsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, canListProducts, searchTerm, searchMode, selectedSectorId, activeLeafId, page, rowsPerPage]);

  // ─── Seçim yardımcıları ───────────────────────────
  const toggleProduct = (product: CatalogBrowseProduct) => {
    setPickedProducts((prev) => {
      const next = new Map(prev);
      if (next.has(product.id)) next.delete(product.id);
      else next.set(product.id, product);
      return next;
    });
  };

  const toggleCategory = (node: CatalogCategoryNode) => {
    setPickedCategories((prev) => {
      const next = new Map(prev);
      if (next.has(node.id)) {
        next.delete(node.id);
        return next;
      }
      next.set(node.id, node);
      // Üst kategori seçilince alt kategorileri ayrıca tutmanın anlamı yok; backend
      // filtresi zaten alt kategorileri kapsıyor, aksi hâlde aynı ürün iki kez sayılır.
      node.children.forEach((child) => next.delete(child.id));
      return next;
    });
  };

  const toggleAllOnPage = () => {
    const allPicked = products.length > 0 && products.every((p) => pickedProducts.has(p.id));
    setPickedProducts((prev) => {
      const next = new Map(prev);
      products.forEach((p) => {
        if (allPicked) next.delete(p.id);
        else next.set(p.id, p);
      });
      return next;
    });
  };

  /** Üst kategorisi seçiliyse alt kategori kutusu da işaretli görünür. */
  const isCategoryCovered = useCallback(
    (node: CatalogCategoryNode, parent?: CatalogCategoryNode) =>
      pickedCategories.has(node.id) || (parent != null && pickedCategories.has(parent.id)),
    [pickedCategories]
  );

  const estimatedCategoryCount = useMemo(
    () => Array.from(pickedCategories.values()).reduce((sum, n) => sum + n.totalProductCount, 0),
    [pickedCategories]
  );

  const estimatedTotal = pickedProducts.size + estimatedCategoryCount;
  const hasSelection = estimatedTotal > 0;

  // ─── Aktarım ──────────────────────────────────────
  const handleTransfer = async () => {
    if (!companyId || !warehouseId || !hasSelection) return;

    setTransferring(true);
    setError(null);
    setResult(null);

    try {
      // 1) Kategori seçimlerini gerçek ürünlere çöz.
      const merged = new Map<number, CatalogBrowseProduct>(pickedProducts);

      const categories = Array.from(pickedCategories.values());
      for (let i = 0; i < categories.length; i += 1) {
        const node = categories[i];
        setProgress({
          phase: `Kategori taranıyor: ${node.name}`,
          current: merged.size,
          total: Math.min(estimatedTotal, MAX_TRANSFER_COUNT),
        });

        const remaining = MAX_TRANSFER_COUNT - merged.size;
        if (remaining <= 0) break;

        const found = await CatalogBrowseService.collectProducts(
          { sectorId: node.sectorId, categoryId: node.id },
          remaining,
          (collected) => {
            setProgress({
              phase: `Kategori taranıyor: ${node.name}`,
              current: merged.size + collected,
              total: Math.min(estimatedTotal, MAX_TRANSFER_COUNT),
            });
          }
        );
        found.forEach((p) => merged.set(p.id, p));
      }

      const toTransfer = Array.from(merged.values()).slice(0, MAX_TRANSFER_COUNT);
      if (toTransfer.length === 0) {
        setError('Aktarılacak ürün bulunamadı.');
        return;
      }

      // 2) Kategori ağacını kiracının envanterinde kur.
      // Ürünler sektör → kategori → alt kategori yapısıyla birlikte inmeli; bu adım
      // olmadan stok kartları kategorisiz kalıyordu.
      setProgress({ phase: 'Kategoriler hazırlanıyor', current: 0, total: toTransfer.length });

      const categoryIdByPath = await resolveCategoryIds(
        companyId,
        toTransfer.map(buildProductCategoryPath).filter((path) => path.length > 0),
        (createdCount, totalCount) =>
          setProgress({
            phase: `Kategoriler oluşturuluyor (${createdCount}/${totalCount})`,
            current: 0,
            total: toTransfer.length,
          })
      );

      // 3) Envanter DTO'suna çevir.
      const parsedQuantity = Number.parseFloat(quantity.replace(',', '.'));
      const openingQuantity =
        Number.isFinite(parsedQuantity) && parsedQuantity >= 0 ? parsedQuantity : DEFAULT_QUANTITY;

      const payload: BulkCreateItemDto[] = toTransfer.map((p) => ({
        // Katalog kodu 23 binin tamamında dolu ve benzersiz; yine de boş gelirse
        // katalog Id'sinden türetilerek stok kodu çakışması engellenir.
        stockCode: p.catalogCode?.trim() || `KTL-${p.id}`,
        name: p.name,
        categoryId: categoryIdByPath.get(buildProductCategoryPath(p)) ?? undefined,
        quantity: openingQuantity,
        salePrice: TRANSFER_SALE_PRICE,
        saleCurrency: 'TRY',
        unit: p.unit || 'Adet',
        barcode: p.barcode || undefined,
        vatRate: p.vatRate,
        vatStatus: mapVatStatus(p.vatStatus),
        departmentNo: p.departmentNo ?? undefined,
        imageUrl: p.imageUrl || undefined,
        isActive: true,
      }));

      // 4) Batch'ler hâlinde kaydet.
      let created = 0;
      const failed: TransferResult['failed'] = [];

      for (let i = 0; i < payload.length; i += TRANSFER_BATCH_SIZE) {
        const batch = payload.slice(i, i + TRANSFER_BATCH_SIZE);
        setProgress({ phase: 'Stoğunuza aktarılıyor', current: i, total: payload.length });

        const response = await InventoryService.bulkCreateItems(companyId, warehouseId, batch);
        created += response.successCount ?? 0;
        (response.failedItems ?? []).forEach((item) =>
          failed.push({ name: item.name, stockCode: item.stockCode, error: item.error })
        );
      }

      setProgress({ phase: 'Stoğunuza aktarılıyor', current: payload.length, total: payload.length });
      setResult({ created, failed });
      setPickedProducts(new Map());
      setPickedCategories(new Map());
    } catch (err: any) {
      console.error('Katalog aktarımı başarısız:', err);
      setError(err?.response?.data?.message || err?.message || 'Aktarım sırasında hata oluştu.');
    } finally {
      setTransferring(false);
      setProgress(null);
    }
  };

  // ════════════════════════════════════════════════════════════════
  // Render
  // ════════════════════════════════════════════════════════════════

  const progressPercent =
    progress && progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  const allOnPagePicked = products.length > 0 && products.every((p) => pickedProducts.has(p.id));
  const someOnPagePicked = products.some((p) => pickedProducts.has(p.id));

  const renderCategoryRow = (node: CatalogCategoryNode, parent?: CatalogCategoryNode) => {
    const covered = isCategoryCovered(node, parent);
    const lockedByParent = parent != null && pickedCategories.has(parent.id);
    const expanded = expandedRoots.has(node.id);
    const isActive = activeLeafId === node.id;

    return (
      <Box key={node.id}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            pl: parent ? 3 : 0.5,
            pr: 0.5,
            py: 0.25,
            borderRadius: 1,
            bgcolor: isActive ? 'action.selected' : 'transparent',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Tooltip title="Bu kategorinin tamamını aktarmak için işaretle">
            <span>
              <Checkbox
                size="small"
                checked={covered}
                disabled={lockedByParent}
                onChange={() => toggleCategory(node)}
              />
            </span>
          </Tooltip>

          <Box
            onClick={() => {
              if (node.isLeaf) {
                // Arama açıkken tablo arama sonucunu gösterir; kategoriye tıklamanın
                // sonuçsuz kalmaması için arama temizlenir.
                setSearchInput('');
                setSearchTerm('');
                setActiveLeafId(isActive ? null : node.id);
              } else {
                setExpandedRoots((prev) => {
                  const next = new Set(prev);
                  if (next.has(node.id)) next.delete(node.id);
                  else next.add(node.id);
                  return next;
                });
              }
            }}
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              minWidth: 0,
            }}
          >
            <Typography
              variant="body2"
              noWrap
              sx={{ flex: 1, fontWeight: isActive ? 700 : 400 }}
              title={node.name}
            >
              {node.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {node.totalProductCount.toLocaleString('tr-TR')}
            </Typography>
            {!node.isLeaf && (expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />)}
          </Box>
        </Box>

        {!node.isLeaf && (
          <Collapse in={expanded} unmountOnExit>
            {node.children.map((child) => renderCategoryRow(child, node))}
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
          Katalogdan Ürün Ekle
        </Typography>
        <GuideButton guideKey="inventory.catalogPicker" size="small" sx={{ ml: 0.5 }} />
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={handleClose} disabled={transferring} aria-label="Kapat">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, height: '72vh', display: 'flex', flexDirection: 'column' }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ m: 1.5, mb: 0 }}>
            {error}
          </Alert>
        )}

        {result && (
          <Alert
            severity={result.failed.length > 0 ? 'warning' : 'success'}
            onClose={() => setResult(null)}
            sx={{ m: 1.5, mb: 0 }}
          >
            <AlertTitle>Aktarım tamamlandı</AlertTitle>
            {result.created.toLocaleString('tr-TR')} ürün stoğunuza eklendi
            {result.failed.length > 0 && (
              <>
                , {result.failed.length.toLocaleString('tr-TR')} ürün atlandı (çoğunlukla
                barkod veya stok kodu zaten stoğunuzda olduğu için).
              </>
            )}
          </Alert>
        )}

        {progress && (
          <Box sx={{ px: 1.5, pt: 1.5 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {progress.phase} — {progress.current.toLocaleString('tr-TR')} /{' '}
              {progress.total.toLocaleString('tr-TR')}
            </Typography>
            <LinearProgress variant="determinate" value={progressPercent} />
          </Box>
        )}

        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* ═══ Sol: gezinme ═══ */}
          <Box
            ref={navPanelRef}
            sx={{
              width: 380,
              flexShrink: 0,
              borderRight: 1,
              borderColor: 'divider',
              overflowY: 'auto',
              p: 1.5,
            }}
          >
            <Typography variant="overline" color="text.secondary">
              1. Sektör
            </Typography>
            {sectorsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={22} />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 1,
                  mt: 0.75,
                  mb: 2,
                }}
              >
                {sectors.map((sector) => {
                  const active = selectedSectorId === sector.id;
                  return (
                    <ButtonBase
                      key={sector.id}
                      onClick={() =>
                        setSelectedSectorId((prev) => (prev === sector.id ? null : sector.id))
                      }
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        gap: 0.25,
                        width: '100%',
                        minHeight: 60,
                        px: 1.25,
                        py: 1,
                        borderRadius: 2,
                        border: 1,
                        borderColor: active ? 'primary.main' : 'divider',
                        bgcolor: active ? 'primary.main' : 'background.paper',
                        color: active ? 'primary.contrastText' : 'text.primary',
                        boxShadow: active ? 2 : 0,
                        transition: 'background-color .15s, border-color .15s, box-shadow .15s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: active ? 'primary.dark' : 'action.hover',
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          lineHeight: 1.25,
                          textAlign: 'left',
                          width: '100%',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {sector.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ lineHeight: 1.2, opacity: active ? 0.9 : 0.6 }}
                      >
                        {sector.productCount.toLocaleString('tr-TR')} ürün
                      </Typography>
                    </ButtonBase>
                  );
                })}
              </Box>
            )}

            {selectedSectorId != null && (
              <Box ref={categorySectionRef}>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="overline" color="text.secondary">
                  2. Kategori → alt kategori
                </Typography>
                {categoriesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={22} />
                  </Box>
                ) : (
                  <Box sx={{ mt: 0.5 }}>
                    {categoryTree.map((node) => renderCategoryRow(node))}
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Ürünleri görmek için alt kategoriye kadar in. Kutuyu işaretlemek o
                  kategorinin tamamını listelemeden aktarır.
                </Typography>
              </Box>
            )}
          </Box>

          {/* ═══ Sağ: arama + tablo ═══ */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Box sx={{ display: 'flex', gap: 1, p: 1.5, alignItems: 'center' }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={searchMode}
                onChange={(_, value) => value && setSearchMode(value)}
              >
                <ToggleButton value="name">Ürün Adı</ToggleButton>
                <ToggleButton value="barcode">Barkod</ToggleButton>
              </ToggleButtonGroup>
              <TextField
                size="small"
                fullWidth
                placeholder={
                  searchMode === 'name' ? 'Ürün adıyla ara...' : 'Barkod ile ara...'
                }
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Divider />

            {!canListProducts ? (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <SearchIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Ürünler henüz yüklenmedi
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
                  Katalog 23 binden fazla ürün taşıyor, bu yüzden liste kendiliğinden
                  açılmaz. Soldan bir alt kategori seçin ya da ürün adı veya barkodla arayın.
                </Typography>
              </Box>
            ) : (
              <>
                {productsLoading && <LinearProgress />}
                <TableContainer sx={{ flex: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={allOnPagePicked}
                            indeterminate={someOnPagePicked && !allOnPagePicked}
                            onChange={toggleAllOnPage}
                          />
                        </TableCell>
                        <TableCell>Ürün Adı</TableCell>
                        <TableCell sx={{ width: 150 }}>Barkod</TableCell>
                        <TableCell sx={{ width: 220 }}>Kategori</TableCell>
                        <TableCell sx={{ width: 80 }}>Birim</TableCell>
                        <TableCell sx={{ width: 70 }} align="right">
                          KDV
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {products.map((product) => {
                        const picked = pickedProducts.has(product.id);
                        return (
                          <TableRow
                            key={product.id}
                            hover
                            selected={picked}
                            onClick={() => toggleProduct(product)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox size="small" checked={picked} />
                            </TableCell>
                            <TableCell>{product.name}</TableCell>
                            <TableCell>{product.barcode || '-'}</TableCell>
                            <TableCell>{product.categoryPath || '-'}</TableCell>
                            <TableCell>{product.unit}</TableCell>
                            <TableCell align="right">%{product.vatRate}</TableCell>
                          </TableRow>
                        );
                      })}
                      {!productsLoading && products.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              Sonuç bulunamadı.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  labelRowsPerPage="Sayfa başına"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from.toLocaleString('tr-TR')}-${to.toLocaleString('tr-TR')} / ${count.toLocaleString('tr-TR')}`
                  }
                />
              </>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, gap: 1.5, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <Select
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
            displayEmpty
            startAdornment={
              <InputAdornment position="start">
                <WarehouseIcon fontSize="small" />
              </InputAdornment>
            }
          >
            <MenuItem value="" disabled>
              Depo seçin
            </MenuItem>
            {warehouses.map((warehouse) => (
              <MenuItem key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
                {warehouse.isDefault ? ' (varsayılan)' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Miktar"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          sx={{ width: 110 }}
          inputProps={{ inputMode: 'decimal' }}
          helperText="Boşsa 1"
        />

        <Box sx={{ flex: 1, minWidth: 200 }}>
          {hasSelection ? (
            <Typography variant="body2">
              <strong>{pickedProducts.size.toLocaleString('tr-TR')}</strong> ürün
              {pickedCategories.size > 0 && (
                <>
                  {' + '}
                  <strong>{pickedCategories.size}</strong> kategori (≈
                  {estimatedCategoryCount.toLocaleString('tr-TR')} ürün)
                </>
              )}
              {estimatedTotal > MAX_TRANSFER_COUNT && (
                <Typography component="span" variant="body2" color="warning.main">
                  {' '}
                  — tek seferde en fazla {MAX_TRANSFER_COUNT.toLocaleString('tr-TR')} ürün
                  aktarılır
                </Typography>
              )}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Ürün veya kategori seçin.
            </Typography>
          )}
        </Box>

        <Button
          color="inherit"
          onClick={() => {
            setPickedProducts(new Map());
            setPickedCategories(new Map());
          }}
          disabled={transferring || !hasSelection}
        >
          Seçimi Temizle
        </Button>
        <Button color="inherit" onClick={handleClose} disabled={transferring}>
          Kapat
        </Button>
        <Button
          variant="contained"
          onClick={handleTransfer}
          disabled={transferring || !hasSelection || !warehouseId || !companyId}
        >
          {transferring ? 'Aktarılıyor...' : 'Stoğuma Aktar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CatalogPickerModal;
